import React, { useEffect } from "react";
import { Box, Grid, Icon } from "@mui/material";
import Divider from "@material-ui/core/Divider";

import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
import SoftButton from "components/SoftComponents/SoftButton";
import SoftInput from "components/SoftComponents/SoftInput";

import { Controller, useFieldArray, useFormState } from "react-hook-form";
import breakpoints from "assets/theme/base/breakpoints";

const days = [
    { label: "Mo", field: "monday" },
    { label: "Tu", field: "tuesday" },
    { label: "We", field: "wednesday" },
    { label: "Th", field: "thursday" },
    { label: "Fr", field: "friday" },
    { label: "Sa", field: "saturday" },
    { label: "Su", field: "sunday" }
];

// Helper to render error text cleanly
const ErrorMessage = ({ error }) => {
    if (!error) return null;
    return (
        <SoftTypography variant="caption" color="error" fontWeight="medium" sx={{ display: 'block', mt: 0.5 }}>
            {error.message}
        </SoftTypography>
    );
};

// 1. Editor for "startEnd" (Specific Date & Time)
const StartEndEditor = ({ control, deviceLayout }) => {
    const { fields, append, remove } = useFieldArray({
        control,
        name: "scheduling.startEnd",
    });

    // Extract errors for contextual highlighting
    const { errors } = useFormState({ control });

    useEffect(() => {
        if (!fields.length) {
            append({ startTime: "", endTime: "" });
        }
    }, [fields, control]);

    return (
        <SoftBox>
            {fields.map((item, index) => {
                const itemErrors = errors?.scheduling?.startEnd?.[index];

                return (
                    <SoftBox sx={{ border: { xxs: "1px solid #ccc", md: "none" }, borderRadius: "10px", mb: 2 }}>
                        <Grid container spacing={1} key={item.id} alignItems="flex-start" sx={{ p: { xxs: 1, md: 0 } }}>
                            <Grid item xxs={12} md={5}>
                                <SoftTypography variant="caption" fontWeight="bold">Start Date & Time</SoftTypography>
                                <Controller
                                    name={`scheduling.startEnd.${index}.startTime`}
                                    control={control}
                                    rules={{ required: "Start time is required" }}
                                    render={({ field }) => (
                                        <SoftBox>
                                            <SoftInput type="datetime-local" {...field} fullWidth error={!!itemErrors?.startTime} />
                                            <ErrorMessage error={itemErrors?.startTime} />
                                        </SoftBox>
                                    )}
                                />
                            </Grid>
                            <Grid item xxs={12} md={5}>
                                <SoftTypography variant="caption" fontWeight="bold">End Date & Time</SoftTypography>
                                <Controller
                                    name={`scheduling.startEnd.${index}.endTime`}
                                    control={control}
                                    rules={{
                                        required: "End time is required",
                                        validate: (value, formValues) => {
                                            const start = formValues?.scheduling?.startEnd?.[index]?.startTime;
                                            if (!start || !value) return true;
                                            return new Date(value) > new Date(start) || "End time must be after start time";
                                        }
                                    }}
                                    render={({ field }) => (
                                        <SoftBox>
                                            <SoftInput type="datetime-local" {...field} fullWidth error={!!itemErrors?.endTime} />
                                            <ErrorMessage error={itemErrors?.endTime} />
                                        </SoftBox>
                                    )}
                                />
                            </Grid>
                            <Grid item xxs={12} md={2} sx={{ mt: { xxs: 0, md: 2 } }}>
                                <SoftButton variant="outlined" color="error" onClick={() => remove(index)} fullWidth>
                                    <Icon>delete</Icon>
                                </SoftButton>
                            </Grid>
                        </Grid>
                    </SoftBox>
                );
            })}
            <SoftButton
                variant="outlined" color="secondary" size="small" sx={{ mt: 1 }}
                onClick={() => append({ startTime: "", endTime: "" })}
            >
                Add Slot <Icon sx={{ ml: 1 }}>add</Icon>
            </SoftButton>
        </SoftBox>
    );
};

// 2. Editor for "recurring" (Days of week + Time)
const RecurringEditor = ({ control, deviceLayout }) => {
    const { fields, append, remove } = useFieldArray({
        control,
        name: "scheduling.recurring",
    });

    const { errors } = useFormState({ control });

    useEffect(() => {
        if (!fields.length) {
            append({ days: {}, startTime: "", endTime: "" });
        }
    }, [fields, control]);

    return (
        <SoftBox>
            {deviceLayout != 'mobile' && (
                <Grid container spacing={2} sx={{ mb: 1, px: 1 }}>
                    {days.map((day, index) => (
                        <Grid item key={index} xl={0.7}><SoftTypography variant="caption" fontWeight="bold">{day.label}</SoftTypography></Grid>
                    ))}
                    <Grid item xl={3}><SoftTypography variant="caption" fontWeight="bold">Start Time</SoftTypography></Grid>
                    <Grid item xl={3}><SoftTypography variant="caption" fontWeight="bold">End Time</SoftTypography></Grid>
                </Grid>
            )}
            {fields.map((item, index) => {
                const itemErrors = errors?.scheduling?.recurring?.[index];

                return (
                    <SoftBox p={{ xxs: 2, xl: 0 }} sx={{ border: { xxs: "1px solid #ccc", xl: "none" }, borderRadius: "10px", mb: 2 }}>
                        <Grid container spacing={1} key={item.id} alignItems="flex-start" sx={{ p: { xxs: 1, xl: 0 } }}>
                            {days.map((day, dayIndex) => (
                                <Grid item key={day.field} xxs={dayIndex < 2 || dayIndex >= 5 ? 6 : 4} xs={dayIndex < 4 ? 3 : 4} sm={1.71} xl={0.7} sx={{ mt: { xs: 0, xl: 0.5 } }}>
                                    <Controller
                                        name={`scheduling.recurring.${index}.days.${day.field}`}
                                        control={control}
                                        render={({ field }) => (
                                            <SoftButton
                                                size="small" variant={field.value ? "gradient" : "contained"} color={field.value ? "dark" : "light"}
                                                onClick={() => field.onChange(!field.value)} sx={{ minWidth: "32px", p: 0 }} fullWidth
                                            >
                                                <SoftBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ color: field.value ? "light" : "dark" }}>
                                                    <Icon sx={{ fontSize: "28px !important", color: field.value ? "#e9ecef !important" : "#344767 !important" }}>{field.value ? "check" : "close"}</Icon>
                                                    {deviceLayout == 'mobile' && (
                                                        <SoftTypography variant="caption" fontWeight="bold" color={field.value ? "light" : "dark"} sx={{ fontSize: '9px' }}>{day.field}</SoftTypography>
                                                    )}
                                                </SoftBox>
                                            </SoftButton>
                                        )}
                                    />
                                </Grid>
                            ))}
                            <Grid item xxs={12} md={5.5} xl={3}>
                                {deviceLayout == 'mobile' && <SoftTypography variant="caption" fontWeight="bold">Start</SoftTypography>}
                                <Controller
                                    name={`scheduling.recurring.${index}.startTime`}
                                    control={control}
                                    rules={{ required: "Required" }}
                                    render={({ field }) => (
                                        <SoftBox>
                                            <SoftInput type="time" {...field} fullWidth error={!!itemErrors?.startTime} />
                                            <ErrorMessage error={itemErrors?.startTime} />
                                        </SoftBox>
                                    )}
                                />
                            </Grid>
                            <Grid item xxs={12} md={5.5} xl={3}>
                                {deviceLayout == 'mobile' && <SoftTypography variant="caption" fontWeight="bold">End</SoftTypography>}
                                <Controller
                                    name={`scheduling.recurring.${index}.endTime`}
                                    control={control}
                                    rules={{
                                        required: "Required",
                                        validate: (value, formValues) => {
                                            const start = formValues?.scheduling?.recurring?.[index]?.startTime;
                                            if (!start || !value) return true;
                                            // Standard string comparison works perfectly for "HH:mm" vs "HH:mm"
                                            return value > start || "Must be after start";
                                        }
                                    }}
                                    render={({ field }) => (
                                        <SoftBox>
                                            <SoftInput type="time" {...field} fullWidth error={!!itemErrors?.endTime} />
                                            <ErrorMessage error={itemErrors?.endTime} />
                                        </SoftBox>
                                    )}
                                />
                            </Grid>
                            <Grid item xxs={12} md={1}>
                                <SoftBox pt={{ xxs: 0, md: 2, xl: 0 }}>
                                    <SoftButton sx={{ minWidth: '20px !important', paddingLeft: '0 !important', paddingRight: '0 !important' }} variant="outlined" color="error" onClick={() => remove(index)} fullWidth><Icon>delete</Icon></SoftButton>
                                </SoftBox>
                            </Grid>
                        </Grid>
                    </SoftBox>
                );
            })}
            <SoftButton
                variant="outlined" color="secondary" size="small" sx={{ mt: 1 }}
                onClick={() => append({ days: {}, startTime: "", endTime: "" })}
            >
                Add Recurring <Icon sx={{ ml: 1 }}>add</Icon>
            </SoftButton>
        </SoftBox>
    );
};

// 3. Editor for "recurringWithDate" (Days + Date bounds + Time bounds)
const RecurringWithDateEditor = ({ control, deviceLayout }) => {
    const { fields, append, remove } = useFieldArray({
        control,
        name: "scheduling.recurringWithDate",
    });

    const { errors } = useFormState({ control });

    useEffect(() => {
        if (!fields.length) {
            append({ days: {}, startDate: "", endDate: "", startTime: "", endTime: "" });
        }
    }, [fields, control]);

    return (
        <SoftBox>
            {deviceLayout != 'mobile' && (
                <Grid container spacing={1} sx={{ mb: 1, px: 1 }}>
                    {days.map((day, index) => (
                        <Grid item key={index} xl={0.6}><SoftTypography variant="caption" fontWeight="bold">{day.label}</SoftTypography></Grid>
                    ))}
                    <Grid item xl={2}><SoftTypography variant="caption" fontWeight="bold">Start Date</SoftTypography></Grid>
                    <Grid item xl={1.5}><SoftTypography variant="caption" fontWeight="bold">Start Time</SoftTypography></Grid>
                    <Grid item xl={2}><SoftTypography variant="caption" fontWeight="bold">End Date</SoftTypography></Grid>
                    <Grid item xl={1.5}><SoftTypography variant="caption" fontWeight="bold">End Time</SoftTypography></Grid>
                </Grid>
            )}
            {fields.map((item, index) => {
                const itemErrors = errors?.scheduling?.recurringWithDate?.[index];

                return (
                    <SoftBox p={{ xxs: 2, xl: 0 }} sx={{ border: { xxs: "1px solid #ccc", xl: "none" }, borderRadius: "10px", mb: 2 }}>
                        <Grid container spacing={1} key={item.id} alignItems="flex-start" sx={{ p: { xxs: 1, xl: 0 } }}>
                            {days.map((day, dayIndex) => (
                                <Grid item key={day.field} xxs={dayIndex < 2 || dayIndex >= 5 ? 6 : 4} xs={dayIndex < 4 ? 3 : 4} sm={1.71} xl={0.6} sx={{ mt: { xs: 0, xl: 0.5 } }}>
                                    <Controller
                                        name={`scheduling.recurring.${index}.days.${day.field}`}
                                        control={control}
                                        render={({ field }) => (
                                            <SoftButton
                                                size="small" variant={field.value ? "gradient" : "contained"} color={field.value ? "dark" : "light"}
                                                onClick={() => field.onChange(!field.value)} sx={{ minWidth: "32px", p: 0 }} fullWidth
                                            >
                                                <SoftBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ color: field.value ? "light" : "dark" }}>
                                                    <Icon sx={{ fontSize: "28px !important", color: field.value ? "#e9ecef !important" : "#344767 !important" }}>{field.value ? "check" : "close"}</Icon>
                                                    {deviceLayout == 'mobile' && (
                                                        <SoftTypography variant="caption" fontWeight="bold" color={field.value ? "light" : "dark"} sx={{ fontSize: '9px' }}>{day.field}</SoftTypography>
                                                    )}
                                                </SoftBox>
                                            </SoftButton>
                                        )}
                                    />
                                </Grid>
                            ))}
                            <Grid item xxs={12} xs={6} xl={2}>
                                {deviceLayout == 'mobile' && <SoftTypography variant="caption" fontWeight="bold">Start Date</SoftTypography>}
                                <Controller
                                    name={`scheduling.recurringWithDate.${index}.startDate`}
                                    control={control}
                                    rules={{ required: "Required" }}
                                    render={({ field }) => (
                                        <SoftBox>
                                            <SoftInput type="date" {...field} fullWidth error={!!itemErrors?.startDate} />
                                            <ErrorMessage error={itemErrors?.startDate} />
                                        </SoftBox>
                                    )}
                                />
                            </Grid>
                            <Grid item xxs={12} xs={6} xl={1.5}>
                                {deviceLayout == 'mobile' && <SoftTypography variant="caption" fontWeight="bold">Start Time</SoftTypography>}
                                <Controller
                                    name={`scheduling.recurringWithDate.${index}.startTime`}
                                    control={control}
                                    rules={{ required: "Required" }}
                                    render={({ field }) => (
                                        <SoftBox>
                                            <SoftInput type="time" {...field} fullWidth error={!!itemErrors?.startTime} />
                                            <ErrorMessage error={itemErrors?.startTime} />
                                        </SoftBox>
                                    )}
                                />
                            </Grid>
                            <Grid item xxs={12} xs={6} xl={2}>
                                {deviceLayout == 'mobile' && <SoftTypography variant="caption" fontWeight="bold">End Date</SoftTypography>}
                                <Controller
                                    name={`scheduling.recurringWithDate.${index}.endDate`}
                                    control={control}
                                    rules={{
                                        required: "Required",
                                        validate: (value, formValues) => {
                                            const start = formValues?.scheduling?.recurringWithDate?.[index]?.startDate;
                                            if (!start || !value) return true;
                                            return value >= start || "Cannot be before start date";
                                        }
                                    }}
                                    render={({ field }) => (
                                        <SoftBox>
                                            <SoftInput type="date" {...field} fullWidth error={!!itemErrors?.endDate} />
                                            <ErrorMessage error={itemErrors?.endDate} />
                                        </SoftBox>
                                    )}
                                />
                            </Grid>
                            <Grid item xxs={12} xs={6} xl={1.5}>
                                {deviceLayout == 'mobile' && <SoftTypography variant="caption" fontWeight="bold">End Time</SoftTypography>}
                                <Controller
                                    name={`scheduling.recurringWithDate.${index}.endTime`}
                                    control={control}
                                    rules={{
                                        required: "Required",
                                        validate: (value, formValues) => {
                                            const start = formValues?.scheduling?.recurringWithDate?.[index]?.startTime;
                                            if (!start || !value) return true;
                                            return value > start || "Must be after start";
                                        }
                                    }}
                                    render={({ field }) => (
                                        <SoftBox>
                                            <SoftInput type="time" {...field} fullWidth error={!!itemErrors?.endTime} />
                                            <ErrorMessage error={itemErrors?.endTime} />
                                        </SoftBox>
                                    )}
                                />
                            </Grid>
                            <Grid item xxs={12} xl={0.7}>
                                <SoftBox>
                                    <SoftButton sx={{ minWidth: '20px !important', paddingLeft: '0 !important', paddingRight: '0 !important' }} variant="outlined" color="error" onClick={() => remove(index)} fullWidth><Icon>delete</Icon></SoftButton>
                                </SoftBox>
                            </Grid>
                        </Grid>
                    </SoftBox>
                );
            })}
            <SoftButton
                variant="outlined" color="secondary" size="small" sx={{ mt: 1 }}
                onClick={() => append({ days: {}, startDate: "", endDate: "", startTime: "", endTime: "" })}
            >
                Add Slot <Icon sx={{ ml: 1 }}>add</Icon>
            </SoftButton>
        </SoftBox>
    );
};

// Main Interface Component
const ScheduleInterface = ({ control, scheduleType, deviceLayout }) => {
    return (
        <SoftBox py={2} px={1.5} sx={{ backgroundColor: "#f8f9fa", borderRadius: "10px" }}>
            {scheduleType === "alwaysOn" && (
                <SoftBox py={2} display="flex" justifyContent="center">
                    <SoftTypography variant="button" color="text">Campaign will run continuously.</SoftTypography>
                </SoftBox>
            )}

            {scheduleType === "startEnd" && <StartEndEditor control={control} deviceLayout={deviceLayout} />}

            {scheduleType === "recurring" && <RecurringEditor control={control} deviceLayout={deviceLayout} />}

            {scheduleType === "recurringWithDate" && <RecurringWithDateEditor control={control} deviceLayout={deviceLayout} />}
        </SoftBox>
    );
};

export default ScheduleInterface;